import { Organization } from "../model/organization";
import { useAuth0 } from "@auth0/auth0-react";

export function useOrganizationAPI() {
    const { getAccessTokenSilently } = useAuth0();

    /**
     * Get all organizations (admin only)
     */
    const getAllOrganizations = async (): Promise<Organization[]> => {
        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<Organization[]>((resolve, reject) => {
            fetch(`${process.env.REACT_APP_API_URL}/organization/admin/list`, {
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }).then((response) => {
                if (response.status === 200) {
                    response.json().then((data: Organization[]) => {
                        resolve(data);
                    });
                } else if (response.status === 403) {
                    reject(new Error("Not authorized to view organizations"));
                } else {
                    reject(new Error("Failed to fetch organizations"));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    };

    /**
     * Get organization by ID (admin or organization member)
     */
    const getOrganization = async (id: string): Promise<Organization> => {
        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<Organization>((resolve, reject) => {
            fetch(`${process.env.REACT_APP_API_URL}/organization/${id}`, {
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }).then((response) => {
                if (response.status === 200) {
                    response.json().then((data: Organization) => {
                        resolve(data);
                    });
                } else if (response.status === 403) {
                    reject(new Error("Not authorized to view this organization"));
                } else if (response.status === 404) {
                    reject(new Error("Organization not found"));
                } else {
                    reject(new Error("Failed to fetch organization"));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    };

    /**
     * Create a new organization (admin only)
     */
    const createOrganization = async (organization: Organization): Promise<Organization> => {
        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<Organization>((resolve, reject) => {
            try {
                fetch(`${process.env.REACT_APP_API_URL}/organization/admin`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(organization),
                }).then((response) => {
                    if (response.status === 200) {
                        response.json().then((data: Organization) => {
                            resolve(data);
                        });
                    } else if (response.status === 403) {
                        reject(new Error("Not authorized to create organizations"));
                    } else if (response.status === 400) {
                        response.json().then((err: any) => {
                            reject(new Error(err.message || JSON.stringify(err)));
                        }).catch(() => {
                            reject(new Error("Invalid organization data"));
                        });
                    } else {
                        reject(new Error("Failed to create organization"));
                    }
                });
            } catch (error) {
                console.error("Error creating organization:", error);
                reject(error);
            }
        });
    };

    /**
     * Update an organization (admin or organization owner)
     */
    const updateOrganization = async (organization: Organization): Promise<Organization> => {
        if (!organization.id) {
            return Promise.reject(new Error("Organization ID is required for updates"));
        }

        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<Organization>((resolve, reject) => {
            try {
                fetch(`${process.env.REACT_APP_API_URL}/organization/${organization.id}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(organization),
                }).then((response) => {
                    if (response.status === 200) {
                        response.json().then((data: Organization) => {
                            resolve(data);
                        });
                    } else if (response.status === 403) {
                        reject(new Error("Not authorized to update this organization"));
                    } else if (response.status === 404) {
                        reject(new Error("Organization not found"));
                    } else {
                        response.json().then((err: any) => {
                            reject(new Error(err.message || JSON.stringify(err)));
                        }).catch(() => {
                            reject(new Error("Failed to update organization"));
                        });
                    }
                });
            } catch (error) {
                console.error("Error updating organization:", error);
                reject(error);
            }
        });
    };

    /**
     * Delete an organization (admin only)
     */
    const deleteOrganization = async (id: string): Promise<void> => {
        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<void>((resolve, reject) => {
            fetch(`${process.env.REACT_APP_API_URL}/organization/admin/${id}`, {
                method: "DELETE",
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }).then((response) => {
                if (response.status === 200 || response.status === 204) {
                    resolve();
                } else if (response.status === 403) {
                    reject(new Error("Not authorized to delete organizations"));
                } else if (response.status === 404) {
                    reject(new Error("Organization not found"));
                } else if (response.status === 400) {
                    response.json().then((err: any) => {
                        reject(new Error(err.message || JSON.stringify(err)));
                    }).catch(() => {
                        reject(new Error("Cannot delete organization"));
                    });
                } else {
                    reject(new Error("Failed to delete organization"));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    };

    /**
     * Update organization status (active/inactive) - admin only
     */
    const updateOrganizationStatus = async (id: string, isActive: boolean): Promise<Organization> => {
        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<Organization>((resolve, reject) => {
            fetch(`${process.env.REACT_APP_API_URL}/organization/admin/${id}/status?active=${isActive}`, {
                method: "PUT",
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }).then((response) => {
                if (response.status === 200) {
                    response.json().then((data: Organization) => {
                        resolve(data);
                    });
                } else if (response.status === 403) {
                    reject(new Error("Not authorized to update organization status"));
                } else if (response.status === 404) {
                    reject(new Error("Organization not found"));
                } else {
                    reject(new Error("Failed to update organization status"));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    };

    /**
     * Get list of organization types
     */
    const getOrganizationTypes = async (): Promise<string[]> => {
        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUD,
            },
        });

        return new Promise<string[]>((resolve, reject) => {
            fetch(`${process.env.REACT_APP_API_URL}/organization/types`, {
                headers: {
                    authorization: `Bearer ${accessToken}`,
                },
            }).then((response) => {
                if (response.status === 200) {
                    response.json().then((data: string[]) => {
                        resolve(data);
                    });
                } else {
                    reject(new Error("Failed to fetch organization types"));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    };

    return {
        getAllOrganizations,
        getOrganization,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        updateOrganizationStatus,
        getOrganizationTypes,
    };
}